import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";

/** How long the editor has to sit still before an edit is written. */
export const AUTOSAVE_DELAY_MS = 800;

/**
 * Writes pending edits once they have been still for {@link AUTOSAVE_DELAY_MS}.
 * `revision` is anything that changes with every edit — each change restarts
 * the wait, so a burst of typing is one save rather than one per keystroke.
 *
 * `flush` writes now, for an exit that cannot wait out the delay.
 */
export const useAutosave = ({
  pending,
  canSave,
  isSaving,
  revision,
  save,
}: {
  pending: boolean;
  canSave: boolean;
  isSaving: boolean;
  revision: readonly unknown[];
  save: (onError: () => void) => void;
}) => {
  const saveRef = useRef(save);
  const failedRevisionRef = useRef<readonly unknown[] | null>(null);
  const [failed, setFailed] = useImmer(false);
  useEffect(() => {
    saveRef.current = save;
  });

  const sameRevision = (left: readonly unknown[], right: readonly unknown[]) =>
    left.length === right.length &&
    left.every((value, index) => Object.is(value, right[index]));
  const revisionFailed =
    failedRevisionRef.current !== null &&
    sameRevision(failedRevisionRef.current, revision);

  useEffect(() => {
    if (!revisionFailed && failed) {
      setFailed(false);
    }
  }, [failed, revisionFailed, setFailed]);

  const runSave = () => {
    const attemptedRevision = [...revision];
    saveRef.current(() => {
      failedRevisionRef.current = attemptedRevision;
      setFailed(true);
    });
  };

  useEffect(() => {
    if (!pending || !canSave || isSaving || revisionFailed) {
      return;
    }
    const timer = setTimeout(runSave, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pending, canSave, isSaving, revisionFailed, ...revision]);

  return {
    failed,
    flush: () => {
      if (pending && canSave && !isSaving && !revisionFailed) {
        runSave();
      }
    },
  };
};
